import { useContext } from "react";
import {
  FeatureFlagsContext,
  type FeatureFlagsContextValue,
  type FlagsUnavailable,
} from "../contexts/FeatureFlagsContext";
import type { FeatureFlagEvaluation } from "../api/staffBff.service";
import { publicRuntimeConfig } from "../config/publicRuntimeConfig";

/**
 * Hook to check session-scoped feature flag state.
 *
 * Returns:
 * - `hasFlag(key)` — true if the flag is enabled for this session.
 * - `flags` — raw map of evaluated flags keyed by flag key.
 * - `isLoading` — true while the INITIAL fetch is in flight only; the context's
 *   periodic refreshes never set it back to true.
 * - `registrationMaintenanceMode` — mirrors the staff-bff response field of the
 *   same name; true when website registration/login is in maintenance mode.
 * - `registrationMaintenanceNotice` — mirrors the staff-bff response field of the
 *   same name; an estimated return time, or "" when none is set.
 *
 * In `DISABLE_AUTH=true` mode (local dev with auth bypass), `hasFlag`
 * always returns `true` and `isLoading` is always `false`.
 *
 * While `isLoading` is true, `hasFlag` returns `false` so consumers
 * conservatively render the off-state.
 *
 * @example
 * const { hasFlag } = useFeatureFlags();
 *
 * {hasFlag("address-book-live") && (
 *   <PermissionGate permission="addressbook.write">
 *     <Button>Save</Button>
 *   </PermissionGate>
 * )}
 */
export function useFeatureFlags(): {
  hasFlag: (key: string) => boolean;
  flags: Record<string, FeatureFlagEvaluation>;
  isLoading: boolean;
  registrationMaintenanceMode: boolean;
  registrationMaintenanceNotice: string;
} {
  const { flags, isLoading, registrationMaintenanceMode, registrationMaintenanceNotice } =
    useContext<FeatureFlagsContextValue>(FeatureFlagsContext);
  const authDisabled = publicRuntimeConfig.authDisabled;

  const hasFlag = (key: string): boolean => {
    if (authDisabled) {
      return true;
    }
    if (isLoading) {
      return false;
    }
    const flag = flags[key];
    if (!flag) {
      return false;
    }
    return isFlagEnabled(flag);
  };

  return {
    hasFlag,
    flags,
    isLoading: authDisabled ? false : isLoading,
    registrationMaintenanceMode: authDisabled ? false : registrationMaintenanceMode,
    registrationMaintenanceNotice: authDisabled ? "" : registrationMaintenanceNotice,
  };
}

function isFlagEnabled(flag: FeatureFlagEvaluation): boolean {
  if (flag.variantKey === "on") {
    return true;
  }
  if (flag.variantKey === "off") {
    return false;
  }
  if (typeof flag.value === "boolean") {
    return flag.value;
  }
  return false;
}

export function useFeatureFlagsUnavailable(): FlagsUnavailable | null {
  const { flagsUnavailable } = useContext<FeatureFlagsContextValue>(FeatureFlagsContext);
  if (publicRuntimeConfig.authDisabled) return null;
  return flagsUnavailable ?? null;
}

export type FeatureFlagsLoadFailure = {
  failed: boolean;
  retry: () => void;
};

export function useFeatureFlagsLoadFailure(): FeatureFlagsLoadFailure {
  const { flagsLoadFailed, retryFlags } = useContext<FeatureFlagsContextValue>(FeatureFlagsContext);
  return {
    failed: !publicRuntimeConfig.authDisabled && Boolean(flagsLoadFailed),
    retry: retryFlags ?? (() => undefined),
  };
}
